import { createContext, ReactNode, useContext, useState, type JSX } from "react";
import { KeysMatching } from "./tools";

type OnlyObjectKeys<O extends object> = KeysMatching<O, object>;

function useStorageObjectItemStateProxy<S extends object, K extends OnlyObjectKeys<S>>(
    storage: S,
    key: K,
    defaultData?: S[K],
) {
    const [data, setData] = useState<S[K]>(storage[key]);

    const updateData = (newData?: Record<string, unknown>) => {
        setData((currData) => {
            const mergedData = {
                ...currData,
                ...newData,
            };
            storage[key] = mergedData;
            return mergedData;
        });
    };

    const handler: ProxyHandler<any> = {
        get(target: S[K], p: PropertyKey, _receiver: any): any {
            if (p === "update") return updateData;
            return data[p as keyof typeof data] ?? defaultData?.[p as keyof typeof defaultData];
        },
        set(target: S[K], p: PropertyKey, value: any, _receiver: any): boolean {
            updateData({ [p]: value });
            return true;
        },
    };

    return new Proxy({}, handler);
}

type ObjectWithUpdate<S extends object, K extends OnlyObjectKeys<S>> = S[K] & {
    update: (data?: Partial<S[K]>) => void;
};

interface StorageObjectItemContext<S extends object, K extends OnlyObjectKeys<S>> {
    Provider: (props: { children: ReactNode }) => JSX.Element;
    getterHook: () => ObjectWithUpdate<S, K>;
}

export function createStorageObjectItemContext<S extends object, K extends OnlyObjectKeys<S>>(
    storage: S,
    key: K,
    defaultData?: S[K],
): StorageObjectItemContext<S, K> {
    const Context = createContext<S[K]>(undefined);
    return {
        Provider: ({ children }) => {
            const proxy = useStorageObjectItemStateProxy(storage, key, defaultData);
            return <Context.Provider value={proxy}>{children}</Context.Provider>;
        },
        getterHook: () => useContext(Context) as ObjectWithUpdate<S, K>,
    };
}
